'use client';
import React, { useEffect, useRef, useState } from 'react';
import cn from './style.module.scss';

export interface VideoProps {
  src: string;
  poster?: string;
  loop?: boolean;
  muted?: boolean;
  controls?: boolean;
  className?: string;

  showMuteControl?: boolean;
  controlPosition?: 'overlay' | 'inline';
  customControlClassName?: string;
  customControlsWrapperClassName?: string;
  customContainerClassName?: string;
  customWrapperClassName?: string;
  previousPosition?: number;

  showPlayCursorOnHover?: boolean;
  playCursorUrl?: string;
  playCursorSvg?: string;
  playCursorSize?: number;
  playHotspot?: { x: number; y: number };
  onCursorChange?: (active: boolean) => void;
}

const CustomVideo: React.FC<VideoProps> = ({
  src,
  poster = '',
  loop = true,
  muted = true,
  className = '',
  showMuteControl = false,
  controlPosition = 'overlay',
  customControlClassName = '',
  customControlsWrapperClassName = '',
  customContainerClassName = '',
  customWrapperClassName = '',

  showPlayCursorOnHover = false,
  playCursorUrl,
  playCursorSvg,
  playCursorSize = 32,
  playHotspot = { x: 16, y: 16 },
  onCursorChange,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isMuted, setIsMuted] = useState(muted);

  const dataUrlRef = useRef<string | null>(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let played = false;

    const playVideo = () => {
      if (played) return;
      played = true;

      if (src) {
        video.play().catch(() => {
          video.muted = true;
          video.defaultMuted = true;
          setIsMuted(true);
          video.play().catch(() => {
            // Autoplay blocked by the browser; leave the video paused
          });
        });
      } else {
        video.muted = true;
        video.play().catch(() => {
          // Autoplay blocked by the browser; leave the video paused
        });
      }
    };
    if (video.readyState >= 3) {
      playVideo();
    } else {
      video.addEventListener('canplay', playVideo);
      video.addEventListener('loadeddata', playVideo);
    }

    return () => {
      video.removeEventListener('canplay', playVideo);
      video.removeEventListener('loadeddata', playVideo);
    };
  }, [src]);

  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.muted = isMuted;
      v.defaultMuted = isMuted;
    }
  }, [isMuted]);

  useEffect(() => {
    let mounted = true;

    async function svgToPngDataUrl(svgString: string, size = 32) {
      const svgBlob = new Blob([svgString], {
        type: 'image/svg+xml;charset=utf-8',
      });
      const url = URL.createObjectURL(svgBlob);

      try {
        const img = new Image();
        img.src = url;
        await img.decode();

        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas context unavailable');

        ctx.drawImage(img, 0, 0, size, size);
        return canvas.toDataURL('image/png');
      } finally {
        URL.revokeObjectURL(url);
      }
    }

    if (!playCursorSvg) return;

    (async () => {
      try {
        const dataUrl = await svgToPngDataUrl(playCursorSvg, playCursorSize);
        if (!mounted) return;

        dataUrlRef.current = dataUrl;

        const el = containerRef.current;
        if (el && el.matches(':hover')) {
          el.style.setProperty(
            '--custom-cursor',
            `url(${dataUrl}) ${playHotspot.x} ${playHotspot.y}, pointer`
          );
        }
      } catch (err) {
        console.warn('SVG->PNG cursor conversion failed', err);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [playCursorSvg, playCursorSize, playHotspot.x, playHotspot.y]);

  const toggleMute = () => setIsMuted((p) => !p);

  const handleMouseEnter = () => {
    if (!showPlayCursorOnHover) return;
    const el = containerRef.current;
    if (!el) return;

    if (playCursorUrl) {
      el.style.setProperty(
        '--custom-cursor',
        `url(${playCursorUrl}) ${playHotspot.x} ${playHotspot.y}, pointer`
      );
      onCursorChange?.(true);
      return;
    }

    const dataUrl = dataUrlRef.current;
    if (dataUrl) {
      el.style.setProperty(
        '--custom-cursor',
        `url(${dataUrl}) ${playHotspot.x} ${playHotspot.y}, pointer`
      );
      onCursorChange?.(true);
    }
  };

  const handleMouseLeave = () => {
    const el = containerRef.current;
    if (!el) return;
    el.style.removeProperty('--custom-cursor');
    onCursorChange?.(false);
  };

  const shouldRenderControls = !!src && showMuteControl;

  return (
    <div className={`${cn.customVideoWrapper} ${customWrapperClassName}`}>
      <div
        ref={containerRef}
        className={`${cn.customVideoBlock} ${className ?? ''} ${
          showPlayCursorOnHover ? cn.playCursor : ''
        } ${customContainerClassName || ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <video
          ref={videoRef}
          src={src || undefined}
          poster={poster || undefined}
          autoPlay
          loop={loop}
          muted={true}
          playsInline
          webkit-playsinline="true"
          x-webkit-airplay="allow"
          preload="auto"
          controls={false}
          disablePictureInPicture
          className={cn.customVideoElement}
        />
      </div>
      {shouldRenderControls && (
        <div
          className={`${cn.customVideoControls} ${
            cn[`customVideoControls__${controlPosition}`] ?? ''
          } ${customControlsWrapperClassName || ''}`}
        >
          <button
            type="button"
            className={`${cn.customVideoIcon} ${customControlClassName || ''}`}
            onClick={toggleMute}
          >
            {isMuted ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 80.314 62.358"
              >
                <path
                  d="M1580.4,61.882l-22.539-17.531A2.238,2.238,0,0,1,1557,42.59V19.769a2.237,2.237,0,0,1,.859-1.761L1580.4.477V.472a2.233,2.233,0,0,1,3.6,1.762V60.121a2.232,2.232,0,0,1-2.241,2.236A2.193,2.193,0,0,1,1580.4,61.882Zm36.045-12.867L1603.53,36.1l-12.912,12.913a2.265,2.265,0,0,1-3.215-3.191L1600.328,32.9,1587.4,19.975a2.264,2.264,0,1,1,3.215-3.19L1603.53,29.7l12.913-12.912a2.263,2.263,0,1,1,3.212,3.19L1606.732,32.9l12.923,12.924a2.252,2.252,0,0,1-1.589,3.847h-.032A2.254,2.254,0,0,1,1616.443,49.015Zm-72.994-4.838a3.706,3.706,0,0,1-3.45-3.45v-19.1a3.448,3.448,0,0,1,3.45-3.45h7.1a3.706,3.706,0,0,1,3.45,3.45v19.1a3.448,3.448,0,0,1-3.45,3.45Z"
                  transform="translate(-1540 0)"
                />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 70 62.358">
                <path
                  d="M1580.4,61.882l-22.539-17.531A2.238,2.238,0,0,1,1557,42.59V19.769a2.237,2.237,0,0,1,.859-1.761L1580.4.477V.472a2.233,2.233,0,0,1,3.6,1.762V60.121a2.232,2.232,0,0,1-2.241,2.236A2.193,2.193,0,0,1,1580.4,61.882Zm10.6-7.429v-.29a1.839,1.839,0,0,1,1.11-1.7,22.489,22.489,0,0,0,0-41.562A1.825,1.825,0,0,1,1591,9.2V8.915a1.873,1.873,0,0,1,2.6-1.719v.016a26.472,26.472,0,0,1,0,48.96,1.912,1.912,0,0,1-.731.147A1.864,1.864,0,0,1,1591,54.454Zm0-10.284v-.34a1.933,1.933,0,0,1,.77-1.511,13.441,13.441,0,0,0,0-21.282,1.933,1.933,0,0,1-.77-1.512v-.339a1.869,1.869,0,0,1,2.988-1.489,17.443,17.443,0,0,1,0,27.96v0a1.828,1.828,0,0,1-1.116.382A1.869,1.869,0,0,1,1591,44.169Zm-47.551.007a3.706,3.706,0,0,1-3.45-3.45v-19.1a3.448,3.448,0,0,1,3.45-3.45h7.1a3.706,3.706,0,0,1,3.45,3.45v19.1a3.448,3.448,0,0,1-3.45,3.45Z"
                  transform="translate(-1540 0)"
                />
              </svg>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomVideo;
